import { useInfiniteQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAxios } from "../../../api/axios";
import { useTranslation } from "react-i18next";
import useProperty from "../../context/useProperty";

export interface PubPropertyPayload {
  listType: string;
  page: number;
  limit: number;
  searchTxt?: string;
  state?: string;
  offMarket?: boolean;
  occupancy?: string;
  salesInstruction?: string;
  status?: string;
  possession?: string;
  mapBounds: {
    topLeftLng: number;
    topLeftLat: number;
    bottomRightLng: number;
    bottomRightLat: number;
  };
  orderBy?: string;
  order?: string;
}

export const usePublishProperties = ({ type }: { type: string }) => {
  const { coords } = useProperty();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const axios = useAxios(type);

  const urlKey = `${t("property-list-api")}`;
  // const location = useLocation();
  // const queryParams = new URLSearchParams(location.search);
  // const panel = Number(queryParams.get("panel"));
  // const page = Number(queryParams.get("page")) ?? 1;
  // const limit = Number(queryParams.get("limit")) ?? 10;
  const searchTextValue = searchParams.get("searchText");
  const stateValue = searchParams.get("state");
  const offMarketValue = searchParams.get("offMarket");
  const salesInstructionsValue = searchParams.get("salesInstructions");
  const occupancyValue = searchParams.get("occupancy");
  const possessionDeliveryValue = searchParams.get("possessionDelivery");
  const statusValue = searchParams.get("status");
  const sortValue = searchParams.get("order");
  const orderValue = searchParams.get("sort");

  const createPayload = (page: number, limit: number): PubPropertyPayload => {
    let payload: PubPropertyPayload = {
      listType: "list",
      page,
      limit,
      mapBounds: {
        topLeftLng: coords.topLeftLng,
        topLeftLat: coords.topLeftLat,
        bottomRightLat: coords.bottomRightLat,
        bottomRightLng: coords.bottomRightLng
      }
    };

    if (searchTextValue) {
      payload = { ...payload, searchTxt: searchTextValue };
    }
    if (stateValue) {
      payload = { ...payload, state: stateValue };
    }
    if (offMarketValue) {
      payload = { ...payload, offMarket: offMarketValue === "Yes" };
    }
    if (occupancyValue) {
      payload = { ...payload, occupancy: occupancyValue };
    }
    if (salesInstructionsValue) {
      payload = { ...payload, salesInstruction: salesInstructionsValue };
    }
    if (statusValue) {
      payload = { ...payload, status: statusValue };
    }
    if (possessionDeliveryValue) {
      payload = { ...payload, possession: possessionDeliveryValue };
    }
    if (sortValue) {
      payload = { ...payload, order: sortValue.toUpperCase() };
    }
    if (orderValue) {
      payload = { ...payload, orderBy: orderValue };
    }
    return payload;
  };
  const fetchProperties = async ({ pageParam = 1 }) => {
    const payload = createPayload(pageParam, 10); // 10 items per page
    const res = await axios.post(urlKey, payload);
    return {
      data: res.data.data, // Array of properties
      hasNextPage: true, // Boolean indicating if there are more pages
      currentPage: pageParam // Current page number
    };
  };

  const {
    data: arrProperty,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    isPlaceholderData
  } = useInfiniteQuery({
    queryKey: [
      `Property-List`,
      coords,
      // page,
      // limit,
      searchTextValue,
      stateValue,
      offMarketValue,
      occupancyValue,
      salesInstructionsValue,
      statusValue,
      possessionDeliveryValue,
      sortValue
    ],
    queryFn: fetchProperties,
    initialPageParam: 1,
    placeholderData: (oldData) => oldData,
    getNextPageParam: (lastPage: any, allPages: any) => {
      const totalItems = parseInt(allPages[0].data?.properties?.[0]?.full_count, 10) || 0; // Default to 0 if parsing fails
      const totalPages = Math.ceil(totalItems / 10); // Assuming 10 items per page
      const _nextPage =
        lastPage.currentPage < totalPages ? allPages.length + 1 : undefined;
      return _nextPage;
    },
    enabled: !!searchParams && type === "agent",
    refetchOnWindowFocus: false, // Disable refetching when the window regains focus
    refetchOnReconnect: false // Disable refetching when internet connection is restored
  });
  return {
    arrProperty,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    isPlaceholderData
  };
};
